import {
  CreatePaymentsClientPayload,
  createPaymentsClient,
  getPaymentsClientByEmail,
  PaymentsClient,
  PaymentsApiError,
  updatePaymentsClient,
} from "@/api"

export const getApiErrorMessage = (error: unknown) => {
  if (error instanceof PaymentsApiError) {
    if (error.status === 401) return "Session expired. Please log in again."
    // For 403, prefer the server's message (e.g. Plaid identity mismatch with
    // specific guidance) — only fall back to the generic line if none was given.
    if (error.status === 403) return error.message || "You are not authorized to perform this action."
    return error.message
  }
  if (typeof error === "string") return error
  if (error instanceof Error) return error.message
  return "Unexpected error. Please try again."
}

// Build an update payload from the creation payload — only includes fields that
// have actual values so we never overwrite existing data with undefined/null.
const buildUpdatePayload = (payload: CreatePaymentsClientPayload): Partial<CreatePaymentsClientPayload> => {
  const update: Partial<CreatePaymentsClientPayload> = {}
  if (payload.first_name) update.first_name = payload.first_name
  if (payload.last_name) update.last_name = payload.last_name
  if (payload.company_name) update.company_name = payload.company_name
  if (payload.address_line1) update.address_line1 = payload.address_line1
  if (payload.city) update.city = payload.city
  if (payload.state) update.state = payload.state
  if (payload.zip) update.zip = payload.zip
  if (payload.country) update.country = payload.country
  return update
}

export const ensurePaymentsClient = async (payload: CreatePaymentsClientPayload): Promise<PaymentsClient> => {
  const existingClient = await getPaymentsClientByEmail(payload.email)

  if (!existingClient) {
    try {
      return await createPaymentsClient(payload)
    } catch (error) {
      // 409 = email already exists (race condition). Re-fetch and update instead.
      if (error instanceof PaymentsApiError && error.status === 409) {
        const retryClient = await getPaymentsClientByEmail(payload.email)
        if (retryClient) {
          return updatePaymentsClient(retryClient.id, buildUpdatePayload(payload))
        }
      }
      throw error
    }
  }

  return updatePaymentsClient(existingClient.id, buildUpdatePayload(payload))
}
